import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import {
  MapPin,
  Navigation,
  Gauge,
  Clock,
  Activity,
  RefreshCw,
  Play,
  Pause,
  Square,
  Loader2,
  AlertCircle,
  ArrowLeft,
  X,
  Car,
  MapPinOff,
} from "lucide-react";
import { toast } from "react-toastify";

const NO_LOCATION_MESSAGE_PATTERNS = [
  /no location data/i,
  /location data not found/i,
  /not found for this user/i,
];

function isNoLocationApiError(err) {
  const status = err?.response?.status;
  const message = err?.message || "";
  if (status === 403 || status === 404) return true;
  return NO_LOCATION_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

export default function SingleVehicleMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId: deviceIdParam } = useParams();
  const { get } = useApi();
  const { hasAnyRole } = useAuth();

  const deviceId = deviceIdParam ? decodeURIComponent(deviceIdParam) : null;
  const vehicleInfo = location.state || {}; // { vehicle_number, vehicle_name } from mapDashboard

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const routePolylineRef = useRef(null);
  const startPointMarkerRef = useRef(null);
  const routeIndexRef = useRef(0);
  const progressRef = useRef(0);
  const animationFrameRef = useRef(null);
  const isPlayingRef = useRef(true);
  const currentBearingRef = useRef(0);

  const [route, setRoute] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationUnavailable, setLocationUnavailable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isCardOpen, setIsCardOpen] = useState(false); // Location (simple) card
  const [isDetailsCardOpen, setIsDetailsCardOpen] = useState(false); // Device details card
  const [isMobile, setIsMobile] = useState(false);
  const [cardState, setCardState] = useState("normal");
  const [isDragging, setIsDragging] = useState(false);
  const [cardHeight, setCardHeight] = useState(40);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const cardHeightRef = useRef(40);
  const refreshIntervalRef = useRef(null);
  const hasCenteredOnRouteRef = useRef(false);
  const isMapLoadedRef = useRef(false);
  const hasInitialRouteRef = useRef(false);
  const hasAutoOpenedMobileSheetRef = useRef(false);
  const previousPositionRef = useRef(null);
  const markerAnimationFrameRef = useRef(null);
  const routeRef = useRef([]);
  const EPSILON_LAT_LNG = 1e-5;
  const lastLatLngForStatusRef = useRef({ lat: null, lng: null });
  const hasNoLocationRedirectedRef = useRef(false);

  // Status should be derived from coordinate changes (not from speed).
  // If lat/lng didn't change -> Idle, otherwise -> Moving.
  const computeStatusFromLatLng = (lat, lng) => {
    const latNum = typeof lat === "number" ? lat : Number(lat);
    const lngNum = typeof lng === "number" ? lng : Number(lng);
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return "Idle";

    const prev = lastLatLngForStatusRef.current;
    const hasPrev = typeof prev?.lat === "number" && typeof prev?.lng === "number";
    const moved =
      hasPrev &&
      (Math.abs(latNum - prev.lat) > EPSILON_LAT_LNG ||
        Math.abs(lngNum - prev.lng) > EPSILON_LAT_LNG);

    lastLatLngForStatusRef.current = { lat: latNum, lng: lngNum };
    return moved ? "Moving" : "Idle";
  };
  const [currentLocation, setCurrentLocation] = useState({
    lat: null,
    lng: null,
    speed: 0,
    status: "Idle",
    timestamp: new Date().toLocaleTimeString(),
    address: "Loading...",
  });
  const [latestLocationData, setLatestLocationData] = useState(null); // Raw API location for card (IMEI, SIM, etc.)

  const dashboardPath = "/customer/dashboard";
  const mapBasePath = location.pathname.startsWith("/admin") ? "/admin" : "/customer";
  const isAdminUser = hasAnyRole(["admin", "super_admin", "superadmin"]);
  const dashboardRedirectPath = isAdminUser ? "/admin/map/dashboard" : "/customer/map/dashboard";
  const isVehicleHalfPage = location.pathname.includes("vehicle-half");
  const isHalfMobileLayout = isVehicleHalfPage && isMobile;
  const [showHalfActionsUI, setShowHalfActionsUI] = useState(false);
  const [activeExploreAction, setActiveExploreAction] = useState(null);
  const exploreActionTimeoutRef = useRef(null);

  const openPlaybackLatestTrip = async () => {
    try {
      if (!deviceId) {
        toast.error("Device ID missing for playback");
        return;
      }

      // Fetch latest trips for this device and select today's latest first.
      const response = await get(
        `/trips?device_id=${encodeURIComponent(deviceId)}&page=1&per_page=50&sortBy=trip_start_time&sortOrder=desc`
      );
      const r = response;
      const trips = Array.isArray(r)
        ? r
        : Array.isArray(r?.data)
          ? r.data
          : Array.isArray(r?.trips)
            ? r.trips
            : r?.data && Array.isArray(r.data.data)
              ? r.data.data
              : [];

      if (trips.length === 0) {
        toast.info("No trips found for this vehicle");
        return;
      }

      const now = new Date();
      const isToday = (value) => {
        if (!value) return false;
        const d = new Date(value);
        return (
          !Number.isNaN(d.getTime()) &&
          d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate()
        );
      };

      const sortedTrips = [...trips].sort(
        (a, b) =>
          new Date(b?.trip_start_time || b?.startTime || b?.created_at || 0) -
          new Date(a?.trip_start_time || a?.startTime || a?.created_at || 0)
      );
      const todayLatestTrip =
        sortedTrips.find((trip) =>
          isToday(trip?.trip_start_time || trip?.startTime || trip?.created_at)
        ) || sortedTrips[0];

      const latestTripId =
        todayLatestTrip?.id ||
        todayLatestTrip?._id ||
        todayLatestTrip?.trip_id ||
        null;

      if (!latestTripId) {
        toast.error("Unable to open playback: trip ID missing");
        return;
      }

      navigate(
        `${mapBasePath}/map/playback/${encodeURIComponent(deviceId)}?tripId=${encodeURIComponent(latestTripId)}`
      );
    } catch (err) {
      toast.error(err?.message || "Failed to load playback trip");
    }
  };

  const getStartPointIcon = () => {
    const size = 38;
    const radius = 16;
    const StartIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size/2}" cy="${size/2}" r="${radius + 2}" fill="#10b981" opacity="0.3"/>
      <circle cx="${size/2}" cy="${size/2}" r="${radius}" fill="#10b981" stroke="#ffffff" stroke-width="3" opacity="0.95"/>
      <circle cx="${size/2}" cy="${size/2}" r="${radius - 3}" fill="#059669" opacity="0.9"/>
      <text x="${size/2}" y="${size/2 + 6}" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="#ffffff" text-anchor="middle">S</text>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(StartIcon)}`;
  };

  const getCarIconRed = () => {
    const CarIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="64" viewBox="0 0 300 600">
      <ellipse cx="150" cy="300" rx="95" ry="240" fill="#000" opacity="0.15"/>
      <path d="M90 60 Q150 20 210 60 L235 150 Q245 300 235 450 L210 540 Q150 580 90 540 L65 450 Q55 300 65 150 Z" fill="#dc2626" stroke="#991b1b" stroke-width="2"/>
      <path d="M90 60 Q150 20 210 60 L235 150 Q245 300 235 450 L210 540 Q150 580 90 540 L65 450 Q55 300 65 150 Z" fill="#ef4444" opacity="0.3"/>
      <rect x="105" y="110" width="90" height="90" rx="18" fill="#bae6fd" opacity="0.7"/>
      <rect x="105" y="380" width="90" height="90" rx="18" fill="#bae6fd" opacity="0.7"/>
      <rect x="140" y="80" width="20" height="120" rx="10" fill="#ffffff" opacity="0.25"/>
      <rect x="45" y="170" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="45" y="350" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="225" y="170" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="225" y="350" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="52" y="188" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="52" y="368" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="232" y="188" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="232" y="368" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="105" y="60" width="40" height="12" rx="6" fill="#fde047"/>
      <rect x="155" y="60" width="40" height="12" rx="6" fill="#fde047"/>
      <rect x="105" y="528" width="40" height="12" rx="6" fill="#7f1d1d"/>
      <rect x="155" y="528" width="40" height="12" rx="6" fill="#7f1d1d"/>
      <line x1="150" y1="50" x2="150" y2="550" stroke="#ffffff" stroke-width="3" stroke-dasharray="10,10" opacity="0.5"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CarIcon)}`;
  };

  const lerp = (start, end, t) => start + (end - start) * t;
  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const getDistance = (p1, p2) => {
    const R = 6371;
    const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
    const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((p1.lat * Math.PI) / 180) *
        Math.cos((p2.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };
  const getBearing = (p1, p2) => {
    const lat1 = (p1.lat * Math.PI) / 180;
    const lat2 = (p2.lat * Math.PI) / 180;
    const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  };
  const hasSetRotation = (marker) =>
    marker && typeof marker.setRotation === "function";

  // Smoothly animate marker from old position to new position (same as LiveMap)
  const animateMarkerToNewPosition = (marker, oldPos, newPos, duration = 2000, onComplete) => {
    if (!marker || !oldPos || !newPos) {
      onComplete?.();
      return;
    }
    if (markerAnimationFrameRef.current) {
      cancelAnimationFrame(markerAnimationFrameRef.current);
      markerAnimationFrameRef.current = null;
    }
    const startTime = Date.now();
    const startLat = oldPos.lat;
    const startLng = oldPos.lng;
    const endLat = newPos.lat;
    const endLng = newPos.lng;
    let currentBearing = getBearing(oldPos, newPos);

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      const currentLat = lerp(startLat, endLat, easedProgress);
      const currentLng = lerp(startLng, endLng, easedProgress);

      try {
        if (marker && typeof marker.setPosition === "function") {
          marker.setPosition({ lat: currentLat, lng: currentLng });
          if (mapInstance.current) {
            const now = Date.now();
            if (!mapInstance.current.lastPan || now - mapInstance.current.lastPan > 100) {
              mapInstance.current.lastPan = now;
              mapInstance.current.panTo([currentLng, currentLat], { duration: 150 });
            }
          }
        }
      } catch (_) {}

      const targetBearing = getBearing(
        { lat: currentLat, lng: currentLng },
        { lat: endLat, lng: endLng }
      );
      let angleDiff = targetBearing - currentBearing;
      if (angleDiff > 180) angleDiff -= 360;
      if (angleDiff < -180) angleDiff += 360;
      currentBearing = (currentBearing + angleDiff * 0.2 + 360) % 360;

      if (hasSetRotation(marker)) {
        marker.setRotation(currentBearing);
      } else {
        const el = marker?.getElement?.();
        if (el) {
          const img = el.querySelector("img");
          (img || el).style.transform = `rotate(${currentBearing}deg)`;
        }
      }

      if (progress < 1) {
        markerAnimationFrameRef.current = requestAnimationFrame(animate);
      } else {
        markerAnimationFrameRef.current = null;
        currentBearingRef.current = currentBearing;
        onComplete?.();
      }
    };

    markerAnimationFrameRef.current = requestAnimationFrame(animate);
  };

  const fetchLocations = async (showLoading = true) => {
    if (!deviceId) {
      setLocationUnavailable(true);
      setError(null);
      setIsLoading(false);
      return;
    }
    if (showLoading) setIsLoading(true);
    setError(null);
    setLocationUnavailable(false);
    try {
      const response = await get(`/customer/locations?device_id=${encodeURIComponent(deviceId)}`);
      let locations = [];
      if (response?.data != null) {
        locations = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        locations = response;
      }
      if (locations.length === 0) {
        setRoute([]);
        setLatestLocationData(null);
        lastLatLngForStatusRef.current = { lat: null, lng: null };
        setCurrentLocation((prev) => ({
          ...prev,
          speed: 0,
          status: "Idle",
          address: "No location data",
        }));
        setLocationUnavailable(true);
        setError(null);
        setIsLoading(false);
        if (isAdminUser && !hasNoLocationRedirectedRef.current) {
          hasNoLocationRedirectedRef.current = true;
          navigate(dashboardRedirectPath, { replace: true });
        }
        return;
      }
      const sorted = [...locations].sort(
        (a, b) => new Date(a.point_time) - new Date(b.point_time)
      );
      const routePoints = sorted.map((loc) => ({
        lat: Number(loc.latitude),
        lng: Number(loc.longitude),
        address: loc.address || `Point`,
        point_time: loc.point_time,
        device_id: loc.device_id,
        speed: Number.parseFloat(loc.speed) || 0,
      }));

      // Build path from old to current: on polling append new point if position changed; on refresh replace
      const samePosition = (a, b) =>
        Math.abs((a?.lat ?? 0) - (b?.lat ?? 0)) < 1e-5 &&
        Math.abs((a?.lng ?? 0) - (b?.lng ?? 0)) < 1e-5;

      if (showLoading) {
        setRoute(routePoints);
      } else {
        setRoute((prev) => {
          if (prev.length === 0) return routePoints;
          const latest = routePoints[routePoints.length - 1];
          const lastPrev = prev[prev.length - 1];
          if (samePosition(latest, lastPrev)) return prev;
          return [...prev, latest];
        });
      }

      const lastRaw = sorted[sorted.length - 1];
      setLatestLocationData(lastRaw || null);
      const latest = routePoints[routePoints.length - 1];
      const status = computeStatusFromLatLng(latest.lat, latest.lng);
      setCurrentLocation((prev) => ({
        ...prev,
        lat: Number(latest.lat),
        lng: Number(latest.lng),
        speed: latest.speed ?? 0,
        status,
        timestamp: latest.point_time
          ? new Date(latest.point_time).toLocaleTimeString()
          : new Date().toLocaleTimeString(),
        address: latest.address,
      }));
    } catch (err) {
      const status = err?.response?.status;
      if (
        isAdminUser &&
        (status === 403 || status === 404) &&
        !hasNoLocationRedirectedRef.current
      ) {
        hasNoLocationRedirectedRef.current = true;
        navigate(dashboardRedirectPath, { replace: true });
        return;
      }
      if (isNoLocationApiError(err)) {
        setLocationUnavailable(true);
        setError(null);
        return;
      }
      setError(err.message || "Failed to load vehicle location");
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const setupMapWithRoute = (map, routeToUse) => {
    const points = routeToUse ?? route;
    if (!map || !points || points.length === 0) return;

    if (startPointMarkerRef.current) {
      try {
        if (typeof startPointMarkerRef.current.remove === "function")
          startPointMarkerRef.current.remove();
      } catch (_) {}
    }
    startPointMarkerRef.current = null;
    if (points.length > 0) {
      try {
        const startIcon = getStartPointIcon();
        startPointMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: points[0].lat, lng: points[0].lng },
          icon: startIcon,
          width: 38,
          height: 38,
          anchor: [19, 19],
          zIndex: 10,
        });
      } catch (_) {}
    }

    // Route polyline: same options as LiveMap selected-vehicle route (path, #2563eb, 0.9, 6)
    if (routePolylineRef.current) {
      try {
        if (typeof routePolylineRef.current.remove === "function")
          routePolylineRef.current.remove();
      } catch (_) {}
      routePolylineRef.current = null;
    }

    if (points.length > 1) {
      // Same as LiveMap: path is route array (objects with lat, lng)
      try {
        routePolylineRef.current = new window.mappls.Polyline({
          map,
          path: points,
          strokeColor: "#2563eb",
          strokeOpacity: 0.9,
          strokeWeight: 6,
        });
      } catch (err) {
      }
    }

    const latestPoint = points[points.length - 1];
    const initialBearing =
      points.length > 1
        ? getBearing(points[points.length - 2], points[points.length - 1])
        : 0;
    const isRouteExtension = hasInitialRouteRef.current;
    hasInitialRouteRef.current = true;

    // When route extended (new lat/lng from API): animate existing marker to new position like LiveMap
    if (isRouteExtension && vehicleMarkerRef.current && previousPositionRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      const oldPos = previousPositionRef.current;
      const moved = getDistance(oldPos, latestPoint) > 1e-5;
      const onDone = () => {
        previousPositionRef.current = {
          lat: Number(latestPoint.lat),
          lng: Number(latestPoint.lng),
        };
        lastLatLngForStatusRef.current = {
          lat: Number(latestPoint.lat),
          lng: Number(latestPoint.lng),
        };
        routeIndexRef.current = Math.max(0, points.length - 1);
        progressRef.current = 0;
        setCurrentLocation((prev) => ({
          ...prev,
          lat: Number(latestPoint.lat),
          lng: Number(latestPoint.lng),
          speed: latestPoint.speed ?? prev.speed ?? 0,
          status: moved ? "Moving" : "Idle",
          address: latestPoint.address || prev.address,
          timestamp: latestPoint.point_time
            ? new Date(latestPoint.point_time).toLocaleTimeString()
            : new Date().toLocaleTimeString(),
        }));
        if (isPlayingRef.current) {
          animationFrameRef.current = requestAnimationFrame(animateVehicle);
        }
      };
      if (moved) {
        animateMarkerToNewPosition(
          vehicleMarkerRef.current,
          oldPos,
          latestPoint,
          2000,
          onDone
        );
      } else {
        vehicleMarkerRef.current.setPosition?.({ lat: latestPoint.lat, lng: latestPoint.lng });
        onDone();
      }
      return;
    }

    if (vehicleMarkerRef.current) {
      try {
        if (typeof vehicleMarkerRef.current.remove === "function")
          vehicleMarkerRef.current.remove();
      } catch (_) {}
    }
    vehicleMarkerRef.current = null;

    currentBearingRef.current = initialBearing;

    const displayName =
      vehicleInfo.vehicle_number ||
      vehicleInfo.vehicle_name ||
      (deviceId && deviceId.substring(0, 12)) ||
      "Vehicle";

    vehicleMarkerRef.current = new window.mappls.Marker({
      map,
      position: latestPoint,
      icon: getCarIconRed(),
      width: 34,
      height: 68,
      anchor: [17, 34],
      popupHtml: isMobile
        ? `<div style="font-size:12px"><b>${displayName}</b><br/>Speed: ${currentLocation.speed} km/h<br/>Status: ${currentLocation.status}</div>`
        : undefined,
    });

    previousPositionRef.current = { lat: latestPoint.lat, lng: latestPoint.lng };

    // Click on vehicle marker opens the Device details card (and closes Location card)
    const onMarkerClick = () => {
      if (isVehicleHalfPage) {
        setShowHalfActionsUI(false);
        if (isHalfMobileLayout) {
          setCardState("normal");
          setCardHeight(50);
          cardHeightRef.current = 50;
        }
      }
      setIsCardOpen(false);
      setIsDetailsCardOpen(true);
    };
    if (vehicleMarkerRef.current?.addEventListener) {
      vehicleMarkerRef.current.addEventListener("click", onMarkerClick);
    }
    setTimeout(() => {
      try {
        const el = vehicleMarkerRef.current?.getElement?.();
        if (el) {
          el.style.cursor = "pointer";
          el.addEventListener("click", onMarkerClick);
        }
      } catch (_) {}
    }, 200);

    if (hasSetRotation(vehicleMarkerRef.current)) {
      vehicleMarkerRef.current.setRotation(initialBearing);
    } else {
      setTimeout(() => {
        const el = vehicleMarkerRef.current?.getElement?.();
        if (el) {
          const img = el.querySelector("img");
          (img || el).style.transform = `rotate(${initialBearing}deg)`;
        }
      }, 100);
    }

    if (isRouteExtension) {
      progressRef.current = 0;
      routeIndexRef.current = Math.max(0, points.length - 1);
    } else {
      progressRef.current = 0;
      routeIndexRef.current = 0;
    }
    if (isPlayingRef.current && !animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(animateVehicle);
    }
  };

  const applyRouteToMap = () => {
    if (!mapInstance.current) return;
    const r = routeRef.current;
    if (r && r.length > 0) setupMapWithRoute(mapInstance.current, r);
  };

  const centerMapOnLatestRoutePoint = (points) => {
    if (!mapInstance.current || !isMapLoadedRef.current) return;
    if (!points || points.length === 0) return;
    if (hasCenteredOnRouteRef.current) return;

    const latest = points[points.length - 1];
    hasCenteredOnRouteRef.current = true;
    mapInstance.current.setCenter([latest.lng, latest.lat], { duration: 800 });
    mapInstance.current.setZoom(12);
  };

  const animateVehicle = () => {
    if (
      !vehicleMarkerRef.current ||
      !mapInstance.current ||
      !isPlayingRef.current ||
      route.length === 0
    ) {
      animationFrameRef.current = null;
      return;
    }
    const currentIndex = routeIndexRef.current;
    const nextIndex = currentIndex + 1;
    // Stop at current (last) point — do not loop back to start
    if (nextIndex >= route.length) {
      const lastPoint = route[route.length - 1];
      if (lastPoint && typeof vehicleMarkerRef.current.setPosition === "function") {
        vehicleMarkerRef.current.setPosition({ lat: lastPoint.lat, lng: lastPoint.lng });
      }
      animationFrameRef.current = null;
      return;
    }
    const currentPoint = route[currentIndex];
    const nextPoint = route[nextIndex];
    if (!currentPoint || !nextPoint) {
      animationFrameRef.current = null;
      return;
    }
    const distance = getDistance(currentPoint, nextPoint);
    const baseSpeed = 0.002;
    const distanceFactor = Math.min(Math.max(distance / 1.0, 0.5), 2.0);
    const speed = baseSpeed * distanceFactor;
    progressRef.current += speed;

    if (progressRef.current >= 1) {
      progressRef.current = 0;
      routeIndexRef.current = nextIndex;
      setCurrentLocation((prev) => ({
        ...prev,
        address: nextPoint.address || prev.address,
        timestamp: nextPoint.point_time
          ? new Date(nextPoint.point_time).toLocaleTimeString()
          : new Date().toLocaleTimeString(),
      }));
    }

    const easedProgress = easeInOutCubic(progressRef.current);
    const interpolatedLat = lerp(currentPoint.lat, nextPoint.lat, easedProgress);
    const interpolatedLng = lerp(currentPoint.lng, nextPoint.lng, easedProgress);
    const targetBearing = getBearing(currentPoint, nextPoint);
    let angleDiff = targetBearing - currentBearingRef.current;
    if (angleDiff > 180) angleDiff -= 360;
    if (angleDiff < -180) angleDiff += 360;
    currentBearingRef.current =
      (currentBearingRef.current + angleDiff * 0.15 + 360) % 360;

    vehicleMarkerRef.current.setPosition({
      lat: interpolatedLat,
      lng: interpolatedLng,
    });
    if (hasSetRotation(vehicleMarkerRef.current)) {
      vehicleMarkerRef.current.setRotation(currentBearingRef.current);
    } else {
      const el = vehicleMarkerRef.current?.getElement?.();
      if (el) {
        const img = el.querySelector("img");
        (img || el).style.transform = `rotate(${currentBearingRef.current}deg)`;
      }
    }

    const now = Date.now();
    if (!vehicleMarkerRef.current.lastUpdate || now - vehicleMarkerRef.current.lastUpdate > 50) {
      vehicleMarkerRef.current.lastUpdate = now;
      const apiSpeed = Number.parseFloat(nextPoint?.speed ?? currentPoint?.speed ?? 0) || 0;
      setCurrentLocation((prev) => ({
        ...prev,
        lat: interpolatedLat,
        lng: interpolatedLng,
        speed: apiSpeed,
        status: computeStatusFromLatLng(interpolatedLat, interpolatedLng),
        timestamp: new Date().toLocaleTimeString(),
      }));
    }
    if (!mapInstance.current.lastPan || now - mapInstance.current.lastPan > 100) {
      mapInstance.current.lastPan = now;
      mapInstance.current.panTo([interpolatedLng, interpolatedLat], {
        duration: 150,
      });
    }
    animationFrameRef.current = requestAnimationFrame(animateVehicle);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      isPlayingRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setIsPlaying(false);
      setCurrentLocation((prev) => ({ ...prev, status: "Idle" }));
    } else {
      isPlayingRef.current = true;
      setIsPlaying(true);
      setCurrentLocation((prev) => ({ ...prev, status: "Idle" }));
      animationFrameRef.current = requestAnimationFrame(animateVehicle);
    }
  };

  const stopTracking = () => {
    isPlayingRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsPlaying(false);
    routeIndexRef.current = 0;
    progressRef.current = 0;
    if (route.length > 0) {
      const startPoint = route[0];
      if (vehicleMarkerRef.current) {
        vehicleMarkerRef.current.setPosition({
          lat: startPoint.lat,
          lng: startPoint.lng,
        });
      }
      if (mapInstance.current) {
        mapInstance.current.panTo([startPoint.lng, startPoint.lat], {
          duration: 800,
        });
      }
      lastLatLngForStatusRef.current = {
        lat: Number(startPoint.lat),
        lng: Number(startPoint.lng),
      };
      setCurrentLocation({
        lat: Number(startPoint.lat),
        lng: Number(startPoint.lng),
        speed: 0,
        status: "Idle",
        timestamp: startPoint.point_time
          ? new Date(startPoint.point_time).toLocaleTimeString()
          : new Date().toLocaleTimeString(),
        address: startPoint.address || "Location not available",
      });
    }
  };

  const handleDragStart = (e) => {
    isDraggingRef.current = true;
    dragStartYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = currentY - dragStartYRef.current;
    const newHeight = Math.max(20, Math.min(80, 40 - (deltaY / window.innerHeight) * 100));
    cardHeightRef.current = newHeight;
    setCardHeight(newHeight);
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    const finalHeight = cardHeightRef.current;
    if (finalHeight > 60) {
      setCardState("expanded");
      setCardHeight(75);
      cardHeightRef.current = 75;
    } else if (finalHeight < 30) {
      setCardState("collapsed");
      setCardHeight(20);
      cardHeightRef.current = 20;
    } else {
      setCardState("normal");
      setCardHeight(40);
      cardHeightRef.current = 40;
    }
  };

  const expandMobileCard = () => {
    if (cardState === "expanded") {
      setCardState("normal");
      setCardHeight(40);
      cardHeightRef.current = 40;
    } else {
      setCardState("expanded");
      setCardHeight(75);
      cardHeightRef.current = 75;
    }
  };

  const collapseMobileCard = () => {
    if (cardState === "collapsed") {
      setCardState("normal");
      setCardHeight(40);
      cardHeightRef.current = 40;
    } else {
      setCardState("collapsed");
      setCardHeight(20);
      cardHeightRef.current = 20;
    }
  };

  const closeMobileCard = () => {
    setIsDetailsCardOpen(false);
    setCardState("normal");
    if (isHalfMobileLayout) {
      // Half-page UI is a split panel; "close" should switch back to actions.
      setIsCardOpen(true);
      setShowHalfActionsUI(true);
      setCardHeight(50);
      cardHeightRef.current = 50;
      return;
    }
    setIsCardOpen(false);
    setCardHeight(40);
    cardHeightRef.current = 40;
    if (isVehicleHalfPage) setShowHalfActionsUI(true);
  };

  const openLocationCard = () => {
    if (isHalfMobileLayout) {
      // On half-page mobile layout we keep the split panel visible; toggle content only.
      if (showHalfActionsUI) {
        setIsDetailsCardOpen(false);
        setIsCardOpen(true);
        setShowHalfActionsUI(false);
      } else {
        setIsDetailsCardOpen(false);
        setIsCardOpen(true);
        setShowHalfActionsUI(true);
      }
      setCardState("normal");
      setCardHeight(50);
      cardHeightRef.current = 50;
      return;
    }
    setIsDetailsCardOpen(false);
    setIsCardOpen((prev) => !prev);
  };

  const openDetailsCard = () => {
    if (isVehicleHalfPage) {
      setShowHalfActionsUI(false);
      if (isHalfMobileLayout) {
        setCardState("normal");
        setCardHeight(50);
        cardHeightRef.current = 50;
      }
    }
    setIsCardOpen(false);
    setIsDetailsCardOpen((prev) => !prev);
  };

  const formatLocationDateTime = (value) => {
    if (value == null || value === "") return null;
    const s = String(value);
    const normalized = s.includes("T") ? s : s.replace(" ", "T");
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const runExploreAction = async (action) => {
    switch (action) {
      case "gps":
        navigate(`${mapBasePath}/map`);
        break;
      case "engine":
        if (!deviceId) {
          toast.error("Device ID missing for engine access");
          return;
        }
        navigate(`${mapBasePath}/map/engine-access/${encodeURIComponent(deviceId)}`, {
          state: {
            vehicle_number: vehicleInfo?.vehicle_number,
            vehicle_name: vehicleInfo?.vehicle_name,
          },
        });
        break;
      case "details":
        openDetailsCard();
        break;
      case "alerts":
        if (!deviceId) {
          toast.error("Device ID missing for alerts");
          return;
        }
        navigate(`${mapBasePath}/alerts?deviceId=${encodeURIComponent(deviceId)}`, {
          state: {
            device_id: deviceId,
            vehicle_number: vehicleInfo?.vehicle_number,
            vehicle_name: vehicleInfo?.vehicle_name,
          },
        });
        break;
      case "safeZone": {
        if (mapBasePath !== "/customer") {
          toast.info("Safe Zone module is coming soon");
          break;
        }
        const params = new URLSearchParams();
        if (deviceId) params.set("deviceId", deviceId);
        const vehicleId =
          vehicleInfo?.vehicle_id ||
          vehicleInfo?.vehicleId ||
          latestLocationData?.vehicle_id ||
          latestLocationData?.vehicleId;
        if (vehicleId) params.set("vehicleId", vehicleId);
        const qs = params.toString();
        navigate(`/customer/geofences${qs ? `?${qs}` : ""}`, {
          state: {
            device_id: deviceId,
            vehicle_id: vehicleId,
            vehicle_number: vehicleInfo?.vehicle_number,
            vehicle_name: vehicleInfo?.vehicle_name,
          },
        });
        break;
      }
      case "playback":
        await openPlaybackLatestTrip();
        break;
      case "dailyStats":
        if (!deviceId) {
          toast.error("Device ID missing for daily stats");
          return;
        }
        navigate(`${mapBasePath}/map/vehicle-stats/${encodeURIComponent(deviceId)}`, {
          state: {
            vehicle_number: vehicleInfo?.vehicle_number,
            vehicle_name: vehicleInfo?.vehicle_name,
          },
        });
        break;
      default:
        break;
    }
  };

  const handleExploreAction = (action) => {
    setActiveExploreAction(action);
    if (exploreActionTimeoutRef.current) {
      clearTimeout(exploreActionTimeoutRef.current);
    }
    exploreActionTimeoutRef.current = setTimeout(async () => {
      await runExploreAction(action);
      setActiveExploreAction((prev) => (prev === action ? null : prev));
      exploreActionTimeoutRef.current = null;
    }, 180);
  };

  useEffect(() => {
    const check = () =>
      setIsMobile(
        window.innerWidth <= 768 ||
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          )
      );
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    return () => {
      if (exploreActionTimeoutRef.current) {
        clearTimeout(exploreActionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDraggingRef.current) handleDragMove(e);
    };
    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) handleDragEnd();
    };
    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("touchmove", handleGlobalMouseMove, { passive: false });
    document.addEventListener("touchend", handleGlobalMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("touchmove", handleGlobalMouseMove);
      document.removeEventListener("touchend", handleGlobalMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!deviceId) {
      setLocationUnavailable(true);
      setIsLoading(false);
      return;
    }
    hasCenteredOnRouteRef.current = false;
    hasInitialRouteRef.current = false;
    hasAutoOpenedMobileSheetRef.current = false;
    hasNoLocationRedirectedRef.current = false;
    previousPositionRef.current = null;
    setLocationUnavailable(false);
    fetchLocations(true);
    refreshIntervalRef.current = setInterval(() => {
      fetchLocations(false);
    }, 5000);
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [deviceId]);

  useEffect(() => {
    if (!window.mappls || mapInstance.current) return;
    const center =
      routeRef.current?.length > 0
        ? [routeRef.current[routeRef.current.length - 1].lng, routeRef.current[routeRef.current.length - 1].lat]
        : [78.486671, 17.385044];
    const map = new window.mappls.Map(mapRef.current, {
      center,
      zoom: 12,
      zoomControl: true,
    });
    mapInstance.current = map;
    map.on("load", () => {
      isMapLoadedRef.current = true;
      applyRouteToMap();
      centerMapOnLatestRoutePoint(routeRef.current);
    });
    return () => {
      isMapLoadedRef.current = false;
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      if (markerAnimationFrameRef.current)
        cancelAnimationFrame(markerAnimationFrameRef.current);
      if (routePolylineRef.current?.remove) routePolylineRef.current.remove();
      if (startPointMarkerRef.current?.remove)
        startPointMarkerRef.current.remove();
      if (vehicleMarkerRef.current?.remove) vehicleMarkerRef.current.remove();
    };
  }, []);

  useEffect(() => {
    routeRef.current = route;
    if (mapInstance.current && route.length > 0) {
      setupMapWithRoute(mapInstance.current, route);
      centerMapOnLatestRoutePoint(route);
    }
  }, [route]);

  // Keep map canvas in sync when mobile sheet/layout changes.
  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;
    const resizeMap = () => {
      if (typeof map.resize === "function") map.resize();
    };

    // Delay a bit to let CSS height transitions settle.
    resizeMap();
    const timeoutId = setTimeout(resizeMap, 320);
    return () => clearTimeout(timeoutId);
  }, [isMobile, isHalfMobileLayout, cardState, cardHeight, isCardOpen, isDetailsCardOpen]);

  // Mobile UX: open the existing bottom sheet by default (expanded) once we have route data.
  // This intentionally does not change any map/marker animation logic.
  useEffect(() => {
    if (!isMobile) return;
    if (!isVehicleHalfPage) return;
    if (!deviceId) return;
    if (route.length === 0) return;
    if (hasAutoOpenedMobileSheetRef.current) return;

    hasAutoOpenedMobileSheetRef.current = true;
    setShowHalfActionsUI(true);
    // Default mobile UX: show the location UI in the bottom half.
    // Keep device-details hidden (location-only) and do not open "expanded" mode.
    setIsCardOpen(true);
    setIsDetailsCardOpen(false);
    setCardState("normal");
    setCardHeight(50);
    cardHeightRef.current = 50;
  }, [isMobile, deviceId, route.length, isVehicleHalfPage]);

  if (!deviceId) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Card className="p-6 max-w-md">
          <p className="text-destructive font-medium">Invalid link: missing device.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(dashboardPath)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Map Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const displayName =
    vehicleInfo.vehicle_number ||
    vehicleInfo.vehicle_name ||
    deviceId.substring(0, 16) + (deviceId.length > 16 ? "…" : "");

  const shouldShowMobileSheet =
    isMobile && !isHalfMobileLayout && (isCardOpen || isDetailsCardOpen) && route.length > 0;
  const mobileMapHeightVh = isMobile && isHalfMobileLayout ? 50 : null;

  return (
    <div className={`relative w-full h-screen bg-background ${isHalfMobileLayout ? "flex flex-col" : ""}`}>
      {/* Mobile Card Styles - same as LiveMap */}
      <style>{`
        @media (max-width: 768px) {
          .single-vehicle-mobile-map-container {
            height: 100vh;
            min-height: 100vh;
            transition: height 0.3s ease;
          }
          .single-vehicle-mobile-card {
            height: 40vh;
            min-height: 40vh;
            max-height: 40vh;
            overflow-y: auto;
            overflow-x: hidden;
            transition: transform 0.3s ease, height 0.3s ease;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 20;
            -webkit-overflow-scrolling: touch;
            border-radius: 16px 16px 0 0;
            box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.3);
          }
          .single-vehicle-mobile-card.expanded {
            height: 75vh;
            min-height: 75vh;
            max-height: 75vh;
          }
          .single-vehicle-mobile-card.collapsed {
            height: 20vh;
            min-height: 20vh;
            max-height: 20vh;
          }
          .single-vehicle-mobile-card.normal {
            height: 40vh;
            min-height: 40vh;
            max-height: 40vh;
          }
          .single-vehicle-mobile-card.dragging {
            transition: none;
          }
          .single-vehicle-drag-handle {
            height: 4px;
            background: #e5e7eb;
            border-radius: 2px;
            margin: 8px auto;
            width: 40px;
            cursor: grab;
          }
          .single-vehicle-drag-handle:active {
            cursor: grabbing;
          }
          .single-vehicle-map-blur {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(8px);
            z-index: 5;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
          }
          .single-vehicle-map-blur.active {
            opacity: 1;
            pointer-events: auto;
          }
          .single-vehicle-mobile-card::-webkit-scrollbar {
            width: 6px;
          }
          .single-vehicle-mobile-card::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
          }
          @keyframes singleVehicleSlideInUp {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          .single-vehicle-mobile-card {
            animation: singleVehicleSlideInUp 0.3s ease-out;
          }
          .single-vehicle-mobile-card.half-ui {
            /* Static bottom-half panel for vehicle-half mobile layout. */
            position: relative;
            bottom: auto;
            left: auto;
            right: auto;
            width: 100%;
            z-index: 10;
            box-shadow: none;
            border-radius: 0;
            animation: none;
            overflow-y: auto;
            flex: none;
          }
          @keyframes singleVehicleGlowPulse {
            0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
            50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.6); }
          }
          .single-vehicle-glow-card {
            animation: singleVehicleGlowPulse 2s ease-in-out infinite;
          }
          @keyframes singleVehicleActionPress {
            0% { transform: scale(1); }
            45% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          .single-vehicle-action-btn {
            transition: transform 220ms ease, background-color 220ms ease, box-shadow 220ms ease;
          }
          .single-vehicle-action-btn.active {
            animation: singleVehicleActionPress 220ms ease;
            box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.25), 0 8px 20px rgba(59, 130, 246, 0.18);
            background: rgba(59, 130, 246, 0.08);
          }
        }
      `}</style>
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="p-6">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading vehicle location...</p>
            </div>
          </Card>
        </div>
      )}

      {locationUnavailable && !isLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
          <Card className="w-full max-w-sm border border-muted-foreground/20 shadow-xl">
            <CardContent className="pt-6 pb-6 px-5 flex flex-col items-center text-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <MapPinOff className="h-7 w-7 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <p className="text-base font-semibold text-foreground">Live map unavailable</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Live tracking is not available for{" "}
                  <span className="font-semibold text-foreground">{displayName}</span>.
                  The GPS device may not be connected or has not reported a location yet.
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(dashboardPath)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go back
                </Button>
                <Button
                  className="w-full"
                  onClick={() => fetchLocations(true)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {error && !isLoading && !locationUnavailable && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 max-w-md">
          <Card className="border-2 border-destructive shadow-xl">
            <CardContent className="pt-4 pb-4 px-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-destructive">Error</p>
                <p className="text-xs text-muted-foreground mt-1 break-words">{error}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchLocations(true)}
                title="Retry"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div
        ref={mapRef}
        id="single-vehicle-map"
        className="w-full h-full single-vehicle-mobile-map-container"
        style={{
          borderRadius: "12px",
          ...(isMobile && mobileMapHeightVh != null ? { height: `${mobileMapHeightVh}vh`, minHeight: `${mobileMapHeightVh}vh` } : {}),
        }}
      />

      {isMobile && cardState === "expanded" && (isCardOpen || isDetailsCardOpen) && route.length > 0 && (
        <div className="single-vehicle-map-blur active md:hidden" style={{ borderRadius: "12px" }} />
      )}

      {/* Mobile: icon-only buttons */}
      <div className="md:hidden absolute top-2 left-2 z-20 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(dashboardPath)}
          className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900 h-9 w-9 p-0 touch-manipulation"
          title="Back to Map Dashboard"
        >
          <ArrowLeft className="h-4 w-4 text-gray-700" />
        </Button>
        {route.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={openLocationCard}
            className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900 h-9 w-9 p-0 touch-manipulation"
            title={isHalfMobileLayout ? (showHalfActionsUI ? "Location" : "Actions") : isCardOpen ? "Hide" : "Location"}
          >
            <Navigation className="h-4 w-4 text-gray-700" />
          </Button>
        )}
      </div>

      {/* Desktop: Back + Location + Device buttons */}
      <div className="hidden md:flex absolute top-4 left-4 z-20 gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate(dashboardPath)} className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900" title="Back to Map Dashboard">
          <ArrowLeft className="h-4 w-4 mr-2 text-gray-700" />
          Back
        </Button>
        {route.length > 0 && (
          <>
            <Button variant="outline" size="sm" onClick={openLocationCard} className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900">
              <Navigation className="h-4 w-4 mr-2 text-gray-700" />
              {isCardOpen ? "Hide" : "Location"}
            </Button>
            <Button variant="outline" size="sm" onClick={openDetailsCard} className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900">
              <Car className="h-4 w-4 mr-2 text-gray-700" />
              {isDetailsCardOpen ? "Hide" : "Device"}
            </Button>
          </>
        )}
      </div>

      {route.length > 0 && (
        <div className="absolute top-2 right-2 md:top-4 md:right-4 z-20">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchLocations(true)}
            className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900 h-9 w-9 p-0 md:h-10 md:w-auto md:px-3 touch-manipulation"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Refresh</span>
          </Button>
        </div>
      )}

      {/* Mobile: Bottom sheet - Location (simple) or Device (detailed) */}
      {isMobile && (isCardOpen || isDetailsCardOpen) && route.length > 0 && (
        <div
          className={`single-vehicle-mobile-card ${
            isHalfMobileLayout && showHalfActionsUI
              ? "bg-background/95 backdrop-blur-sm"
              : "bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
          } border-t border-gray-200 dark:border-gray-700 text-foreground shadow-2xl ${cardState} ${
            isDragging ? "dragging" : ""
          } ${isHalfMobileLayout ? "half-ui" : ""}`}
          style={{
            height: `${cardHeight}vh`,
            minHeight: `${cardHeight}vh`,
            maxHeight: `${cardHeight}vh`,
          }}
        >
          {!isHalfMobileLayout && !(isVehicleHalfPage && showHalfActionsUI) && (
            <div
              className="single-vehicle-drag-handle"
              onTouchStart={(e) => { e.stopPropagation(); handleDragStart(e); }}
              onMouseDown={(e) => { e.stopPropagation(); handleDragStart(e); }}
            />
          )}
          <div className="p-4">
            {isVehicleHalfPage && showHalfActionsUI && (
              <div className="space-y-3">
              

                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                    Status: {currentLocation.status === "Moving" ? "Active" : "Inactive"}
                  </p>
                  <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                    {currentLocation.status === "Moving"
                      ? `Moving ${currentLocation.speed} km/h`
                      : "Idle"}
                  </p>
                </div>

                <div className="pt-1">
                  <p className="text-sm font-semibold text-muted-foreground mb-3">Explore</p>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => handleExploreAction("gps")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "gps" ? "active" : ""}`}
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 shadow-md ring-1 ring-white/30">
                        <Navigation className="h-4.5 w-4.5 text-white" />
                      </span>
                      <span className="mt-1">GPS Tracking</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExploreAction("alerts")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "alerts" ? "active" : ""}`}
                    >
                      <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 shadow-md ring-1 ring-white/30">
                        <AlertCircle className="h-4.5 w-4.5 text-white" />
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-white ring-1 ring-red-400" />
                      </span>
                      <span className="mt-1">Alerts</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExploreAction("safeZone")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "safeZone" ? "active" : ""}`}
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-md ring-1 ring-white/30">
                        <MapPin className="h-4.5 w-4.5 text-white" />
                      </span>
                      <span className="mt-1">Safe Zone</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExploreAction("engine")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "engine" ? "active" : ""}`}
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-700 shadow-md ring-1 ring-white/30">
                        <Car className="h-4.5 w-4.5 text-white" />
                      </span>
                      <span className="mt-1">Engine Access</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleExploreAction("playback")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "playback" ? "active" : ""}`}
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-md ring-1 ring-white/30">
                        <Play className="h-4.5 w-4.5 text-white" />
                      </span>
                      <span className="mt-1">Playback</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExploreAction("dailyStats")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "dailyStats" ? "active" : ""}`}
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-md ring-1 ring-white/30">
                        <Activity className="h-4.5 w-4.5 text-white" />
                      </span>
                      <span className="mt-1">Daily Stats</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExploreAction("details")}
                      className={`single-vehicle-action-btn flex flex-col items-center justify-center text-[10px] text-muted-foreground rounded-lg p-1.5 hover:bg-muted/40 ${activeExploreAction === "details" ? "active" : ""}`}
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-500 to-gray-700 shadow-md ring-1 ring-white/30">
                        <Clock className="h-4.5 w-4.5 text-white" />
                      </span>
                      <span className="mt-1">Details</span>
                    </button>
                    <div className="invisible" aria-hidden />
                  </div>
                </div>

              </div>
            )}

            <div className={isVehicleHalfPage && showHalfActionsUI ? "hidden" : ""}>
              <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg single-vehicle-glow-card">
                    <Navigation className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">{displayName}</h3>
                  <p className="text-[10px] text-gray-600 dark:text-gray-400">
                    {isDetailsCardOpen ? (vehicleInfo.vehicle_name || "Device details") : (vehicleInfo.vehicle_name || "Tracking Active")}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {!isHalfMobileLayout && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={expandMobileCard}
                      className="h-8 w-8 p-0 text-gray-600 dark:text-gray-300"
                      title={cardState === "expanded" ? "Normal" : "Expand"}
                    >
                      ⤢
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={collapseMobileCard}
                      className="h-8 w-8 p-0 text-gray-600 dark:text-gray-300"
                      title={cardState === "collapsed" ? "Normal" : "Collapse"}
                    >
                      ⤡
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={closeMobileCard} className="h-8 w-8 p-0 text-gray-600 dark:text-gray-300" title="Close">×</Button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-1.5">
                  <Activity className={`h-4 w-4 ${currentLocation.status === "Moving" ? "text-green-500 animate-pulse" : "text-gray-500"}`} />
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">Status</span>
                </div>
                <Badge variant={currentLocation.status === "Moving" ? "default" : "secondary"} className="flex items-center gap-1 text-[10px] h-5 px-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${currentLocation.status === "Moving" ? "bg-green-500 animate-pulse" : "bg-gray-500"}`} />
                  {currentLocation.status}
                </Badge>
              </div>
              <div className={`relative p-3 rounded-xl shadow-xl overflow-hidden bg-gradient-to-br ${(currentLocation.speed ?? 0) > 80 ? "from-red-500 via-red-600 to-red-700" : "from-green-400 via-green-500 to-green-600"}`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Gauge className="h-4 w-4 text-white" />
                      <span className="text-xs font-medium text-white/90">Speed</span>
                    </div>
                    <div className="px-2 py-0.5 bg-white/20 rounded-full">
                      <span className="text-[10px] font-semibold text-white">km/h</span>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{currentLocation.speed}</div>
                  <div className="flex items-center gap-1.5 text-[10px] text-white/80">
                    <Clock className="h-2.5 w-2.5" />
                    <span>Updated: {currentLocation.timestamp}</span>
                  </div>
                </div>
              </div>
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MapPin className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">Location</span>
                </div>
                <p className="text-[10px] text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">{currentLocation.address}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <div className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5">Latitude</div>
                    <div className="text-[10px] font-mono font-semibold text-gray-900 dark:text-white">{currentLocation.lat?.toFixed(6) ?? "N/A"}</div>
                  </div>
                  <div className="p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <div className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5">Longitude</div>
                    <div className="text-[10px] font-mono font-semibold text-gray-900 dark:text-white">{currentLocation.lng?.toFixed(6) ?? "N/A"}</div>
                  </div>
                </div>
              </div>
              {/* Full API payload: show on Location + Device mobile sheet (not only Device tab) */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-2.5 rounded-lg border border-purple-200 dark:border-purple-700">
                <h4 className="text-xs font-semibold text-purple-800 dark:text-purple-300 mb-1.5">Vehicle</h4>
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <span className="text-purple-600 dark:text-purple-400 font-medium">Vehicle No</span>
                    <p className="text-gray-900 dark:text-white font-semibold text-xs break-words">{(latestLocationData?.vehicle_number || vehicleInfo.vehicle_number) || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-purple-600 dark:text-purple-400 font-medium">Vehicle name</span>
                    <p className="text-gray-900 dark:text-white font-semibold text-xs break-words">{(latestLocationData?.vehicle_name || vehicleInfo.vehicle_name) || "N/A"}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/80 dark:bg-gray-800/80 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-1.5">Device & GPS</h4>
                <div className="space-y-1.5 text-[10px]">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Device ID</span>
                    <p className="font-mono text-gray-900 dark:text-white break-all leading-snug">{latestLocationData?.device_id || deviceId || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium">IMEI</span>
                    <p className="font-mono text-gray-900 dark:text-white break-all">{latestLocationData?.imei_number || "N/A"}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 font-medium">SIM No</span>
                      <p className="text-gray-900 dark:text-white break-all">{latestLocationData?.sim_number || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 font-medium">SIM card</span>
                      <p className="font-mono text-gray-900 dark:text-white break-all">{latestLocationData?.sim_card || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 font-medium">GPS serial</span>
                      <p className="font-mono text-gray-900 dark:text-white break-all">{latestLocationData?.gps_serial_no || "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-muted/30 dark:bg-muted/20 p-2.5 space-y-1 text-[10px]">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Point time</span>
                  <p className="text-gray-900 dark:text-white">{formatLocationDateTime(latestLocationData?.point_time) || "N/A"}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Activated</span>
                  <p className="text-gray-900 dark:text-white">{formatLocationDateTime(latestLocationData?.activate_date) || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <Button variant="outline" size="sm" onClick={togglePlayPause} className={`flex-1 h-10 text-sm touch-manipulation ${isPlaying ? "bg-green-500 text-white border-green-600 hover:bg-green-600" : "bg-blue-500 text-white border-blue-600 hover:bg-blue-600"}`}>
                  {isPlaying ? <><Pause className="h-4 w-4 mr-2" />Pause</> : <><Play className="h-4 w-4 mr-2" />Play</>}
                </Button>
                <Button variant="outline" size="sm" onClick={stopTracking} className="flex-1 h-10 text-sm bg-red-500 text-white border-red-600 hover:bg-red-600 touch-manipulation">
                  <Square className="h-4 w-4 mr-2" />Stop
                </Button>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">Route Points</span>
                  <span className="text-gray-900 dark:text-white font-bold">{route.length}</span>
                </div>
                {route.length < 2 && (
                  <p className="text-[10px] text-blue-600/80 dark:text-blue-400/80 mt-1.5">Path will show when 2+ locations are available.</p>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Desktop: Simple Location card (when Location button clicked) */}
      {!isMobile && isCardOpen && route.length > 0 && (
        <div className="hidden md:block absolute bottom-24 left-4 z-20 w-full max-w-xs">
          <Card className="border border-muted-foreground/10 shadow-lg bg-background/95 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {displayName}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant={currentLocation.status === "Moving" ? "default" : "secondary"} className="flex items-center gap-1 text-xs">
                    <Activity className={`h-3 w-3 ${currentLocation.status === "Moving" ? "animate-pulse" : ""}`} />
                    {currentLocation.status}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => setIsCardOpen(false)} className="h-8 w-8 p-0">×</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground line-clamp-2">{currentLocation.address}</p>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div><span className="text-muted-foreground">Lat</span><p className="font-mono font-medium">{currentLocation.lat?.toFixed(6)}</p></div>
                <div><span className="text-muted-foreground">Lng</span><p className="font-mono font-medium">{currentLocation.lng?.toFixed(6)}</p></div>
              </div>
              <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Gauge className={`h-4 w-4 ${(currentLocation.speed ?? 0) > 80 ? "text-red-600" : "text-primary"}`} />
                  <span className="text-xs font-medium">Speed</span>
                </div>
                <div className={`text-lg font-bold ${(currentLocation.speed ?? 0) > 80 ? "text-red-600" : "text-primary"}`}>
                  {currentLocation.speed}
                  <span className="text-xs font-normal text-muted-foreground ml-1">km/h</span>
                </div>
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground pt-1 border-t">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Last Updated</span>
                <span className="font-mono">{currentLocation.timestamp}</span>
              </div>
              {route.length < 2 && (
                <p className="text-[10px] text-muted-foreground">Path will show when 2+ locations are available.</p>
              )}
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={togglePlayPause} className="flex-1 text-xs">
                  {isPlaying ? <><Pause className="h-3 w-3 mr-1" />Pause</> : <><Play className="h-3 w-3 mr-1" />Play</>}
                </Button>
                <Button variant="outline" size="sm" onClick={stopTracking} className="flex-1 text-xs">
                  <Square className="h-3 w-3 mr-1" />Stop
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Desktop: Detailed Device card (when Device button clicked or vehicle marker clicked) */}
      {!isMobile && isDetailsCardOpen && route.length > 0 && (
        <div className="hidden md:block absolute bottom-24 left-4 z-30 w-full max-w-xs desktop-marker-detail-card">
          <Card className="border border-muted-foreground/10 shadow-lg bg-background/95 backdrop-blur-sm">
            <CardHeader className="pb-1.5 pt-2 px-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <Navigation className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xs font-semibold text-foreground truncate">
                      {(latestLocationData?.vehicle_number || vehicleInfo.vehicle_number) || "Vehicle"}
                    </CardTitle>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {(latestLocationData?.vehicle_name || vehicleInfo.vehicle_name) || "No name"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant={currentLocation.status === "Moving" ? "default" : "secondary"} className="flex items-center gap-1 text-[10px] h-5 px-1.5">
                    <Activity className={`h-2.5 w-2.5 ${currentLocation.status === "Moving" ? "animate-pulse" : ""}`} />
                    {currentLocation.status}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsDetailsCardOpen(false)}
                    className="h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                    title="Close"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 space-y-1.5">
              {/* Vehicle Information - Compact (same as LiveMap) */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-2 rounded">
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">Vehicle No:</span>
                    <p className="text-gray-900 dark:text-white font-semibold text-[11px]">{(latestLocationData?.vehicle_number || vehicleInfo.vehicle_number) || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">Vehicle Name:</span>
                    <p className="text-gray-900 dark:text-white font-semibold text-[11px]">{(latestLocationData?.vehicle_name || vehicleInfo.vehicle_name) || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* GPS & Device Information - Compact (same as LiveMap) */}
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 p-2 rounded">
                <div className="space-y-1 text-[10px]">
                  <div>
                    <span className="text-purple-600 dark:text-purple-400 font-medium">IMEI:</span>
                    <p className="text-gray-900 dark:text-white font-mono text-[10px]">{latestLocationData?.imei_number || "N/A"}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <span className="text-purple-600 dark:text-purple-400 font-medium">SIM No:</span>
                      <p className="text-gray-900 dark:text-white text-[10px]">{latestLocationData?.sim_number || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-purple-600 dark:text-purple-400 font-medium">GPS Serial:</span>
                      <p className="text-gray-900 dark:text-white text-[10px] font-mono">{latestLocationData?.gps_serial_no || "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information - Compact (same as LiveMap) */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded">
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Lat:</span>
                    <p className="text-gray-900 dark:text-white font-mono text-[10px]">{currentLocation.lat?.toFixed(6) ?? "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Lng:</span>
                    <p className="text-gray-900 dark:text-white font-mono text-[10px]">{currentLocation.lng?.toFixed(6) ?? "N/A"}</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground line-clamp-2 mt-1">{currentLocation.address}</p>
              </div>

              {/* Speed & Controls */}
              <div className="flex items-center justify-between p-2 bg-muted/50 dark:bg-muted/30 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Gauge className={`h-4 w-4 ${(currentLocation.speed ?? 0) > 80 ? "text-red-600 dark:text-red-400" : "text-primary"}`} />
                  <span className="text-xs font-medium">Speed</span>
                </div>
                <div className={`text-lg font-bold ${(currentLocation.speed ?? 0) > 80 ? "text-red-600 dark:text-red-400" : "text-primary"}`}>
                  {currentLocation.speed}
                  <span className="text-xs font-normal text-muted-foreground ml-1">km/h</span>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={togglePlayPause} className="flex-1 text-xs">
                  {isPlaying ? <><Pause className="h-3 w-3 mr-1" />Pause</> : <><Play className="h-3 w-3 mr-1" />Play</>}
                </Button>
                <Button variant="outline" size="sm" onClick={stopTracking} className="flex-1 text-xs">
                  <Square className="h-3 w-3 mr-1" />Stop
                </Button>
              </div>

              {/* Date & Time Information - Compact (same as LiveMap) */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-2 rounded">
                <div className="space-y-1 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">Last Update:</span>
                    <p className="text-gray-900 dark:text-white text-[10px]">
                      {latestLocationData?.point_time
                        ? new Date(latestLocationData.point_time).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : currentLocation.timestamp || "N/A"}
                    </p>
                  </div>
                  {latestLocationData?.activate_date && (
                    <div className="flex justify-between">
                      <span className="text-blue-600 dark:text-blue-400 font-medium">Activated:</span>
                      <p className="text-gray-900 dark:text-white text-[10px]">
                        {new Date(latestLocationData.activate_date).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
